import Map, { NavigationControl } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { useState, useEffect, useRef } from "react";
import MapMarker from "../mapMarker/MapMarker";
import { APIGetStopAddress } from "../../config/utilities/APIs/stopsApi";
import MapSearchBar from "../mapSearchBar/MapSearchBar";

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const MapComponent = ({
  location,
  onPlaceMarker,
  showNavigation,
  existing,
  mode = "edit",
}) => {
  const [marker, setMarker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  const mapRef = useRef();
  const hasCentered = useRef(false);

  useEffect(() => {
    if (!existing) return;
    if (!mapLoaded) return;
    if (!location?.coordinates?.length) return;

    const [longitude, latitude] = location.coordinates;

    if (!isFinite(longitude) || !isFinite(latitude)) return;
    if (hasCentered.current) return;

    setMarker({ longitude, latitude });

    mapRef.current?.flyTo({
      center: [longitude, latitude],
      zoom: 16,
      duration: 1000,
    });

    hasCentered.current = true;
  }, [location, existing, mapLoaded]);

  const handleMapClick = (event) => {
    if (mode === "view") return;

    const { lng, lat } = event.lngLat;
    updateMarkerAndFetchAddress({ latitude: lat, longitude: lng });
  };

  const updateMarkerAndFetchAddress = async (coords) => {
    setMarker(coords);
    setLoading(true);

    try {
      const response = await APIGetStopAddress(
        coords.latitude,
        coords.longitude,
        setLoading,
      );
      const address = response.data?.display_name;
      onPlaceMarker?.(coords, address);
    } catch (err) {
      console.error("Failed to reverse geocode:", err);
      onPlaceMarker?.(coords, null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchResult = (coords, address) => {
    if (mode === "view") return;

    setMarker(coords);
    mapRef.current?.flyTo({
      center: [coords.longitude, coords.latitude],
      essential: true,
      zoom: 16,
      duration: 1000,
    });
    onPlaceMarker?.(coords, address);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        border: "1px solid #ccc",
        border: "0.063rem solid #EAECF0",
        borderTop: "0.094rem solid #EAECF0",
        borderBottom: " 0.094rem solid #EAECF0",
        borderRadius: "0.75rem",
        overflow: "hidden",
      }}
    >
      {mode !== "view" && (
        <div
          style={{
            position: "absolute",
            top: "1rem",
            left: "1rem",
            zIndex: 10,
          }}
        >
          <MapSearchBar
            setLoading={setLoading}
            onSearchResult={handleSearchResult}
            placeholder="Search..."
            width="20rem"
            loading
          />
        </div>
      )}
      <Map
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/asiamobiliti/ckb1yq6080opd1inb3lkdmbii"
        onClick={handleMapClick}
        initialViewState={{
          latitude: 3.1497782289164036,
          longitude: 101.69589354271068,
          zoom: 9,
        }}
        onLoad={() => setMapLoaded(true)}
        ref={mapRef}
      >
        {marker && (
          <MapMarker
            coordinates={marker}
            draggable={mode !== "view"}
            onDrag={mode !== "view" && updateMarkerAndFetchAddress}
          />
        )}
        {showNavigation && <NavigationControl />}
      </Map>
    </div>
  );
};

export default MapComponent;
